import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  Typography,
  Button,
  Box,
  Input,
  FormControl,
  FormLabel,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  LinearProgress,
} from '@mui/joy';
import { UploadSimple as Upload, X, Camera, FileImage, Check, XCircle as AlertCircle, ArrowsClockwise as RefreshCw } from '@phosphor-icons/react';
import { usePhotoBatchUpload, type BatchUploadProgress } from '../../hooks/usePhotoBatchUpload';

interface ChecklistStepPhotoUploadModalProps {
  open: boolean;
  onClose: () => void;
  stepRunId: number;
  jobId?: number;
  opportunityId?: number;
  onUploadSuccess: () => void;
}

// Get status icon for a file based on upload progress
const getFileStatus = (
  file: File,
  index: number,
  progress: BatchUploadProgress
): 'pending' | 'uploading' | 'success' | 'failed' => {
  if (progress.status === 'idle') return 'pending';
  if (progress.status === 'creating_batch') return 'pending';

  // Check if this file failed
  const failedFile = progress.failedFiles.find(f => f.file.name === file.name);
  if (failedFile) return 'failed';

  // Check if currently uploading this file
  if (progress.currentFile === file.name) return 'uploading';

  // Check if this file was already uploaded
  const processedCount = progress.uploaded + progress.failed;
  if (index < processedCount && !failedFile) return 'success';

  return 'pending';
};

export const ChecklistStepPhotoUploadModal: React.FC<ChecklistStepPhotoUploadModalProps> = ({
  open,
  onClose,
  stepRunId,
  jobId,
  opportunityId,
  onUploadSuccess,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [description, setDescription] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { progress, uploadPhotos, retryFailed, reset, isUploading } = usePhotoBatchUpload();

  // Reset the upload state when modal closes
  useEffect(() => {
    if (!open) {
      reset();
      setSelectedFiles([]);
      setDescription('');
    }
  }, [open, reset]);

  const handleFiles = (files: FileList) => {
    const newFiles = Array.from(files).filter(file => {
      // Only allow image files
      if (!file.type.startsWith('image/')) {
        return false;
      }
      // Check file size (max 10MB as per backend validation)
      if (file.size > 10 * 1024 * 1024) {
        return false;
      }
      return true;
    });

    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    const result = await uploadPhotos({
      jobId: jobId?.toString() || null,
      opportunityId: opportunityId?.toString() || null,
      checklistStepRunId: stepRunId,
      files: selectedFiles,
      description: description.trim() || undefined,
    });

    // Only close if all uploads succeeded
    if (result.failed === 0) {
      setSelectedFiles([]);
      setDescription('');
      onUploadSuccess();
      onClose();
    }
  };

  const handleRetry = async () => {
    const result = await retryFailed();

    if (result.failed === 0) {
      setSelectedFiles([]);
      setDescription('');
      onUploadSuccess();
      onClose();
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFiles([]);
      setDescription('');
      reset();
      onClose();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Calculate progress percentage
  const progressPercent = progress.total > 0
    ? Math.round((progress.uploaded / progress.total) * 100)
    : 0;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ minWidth: 600, maxWidth: 800 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography level="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Camera />
            Upload Photos for Step {selectedFiles.length > 0 && `(${selectedFiles.length})`}
          </Typography>
          <IconButton
            variant="plain"
            color="neutral"
            onClick={handleClose}
            disabled={isUploading}
          >
            <X />
          </IconButton>
        </Box>

        {/* Upload Progress */}
        {isUploading && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography level="body-sm" fontWeight="lg">
                Uploading {progress.uploaded} of {progress.total} photos...
              </Typography>
              <Typography level="body-sm" color="neutral">
                {progressPercent}%
              </Typography>
            </Box>
            <LinearProgress
              determinate
              value={progressPercent}
              sx={{ height: 8, borderRadius: 4 }}
            />
            {progress.currentFile && (
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                Uploading: {progress.currentFile}
              </Typography>
            )}
          </Box>
        )}

        {/* Error Display */}
        {progress.status === 'error' && (
          <Alert color="danger" variant="soft" sx={{ mb: 3 }}>
            <Typography level="body-sm">
              {progress.error || 'Failed to upload photos. Please try again.'}
            </Typography>
          </Alert>
        )}

        {/* Partial Failure Display */}
        {progress.status === 'partial_failure' && (
          <Alert color="warning" variant="soft" sx={{ mb: 3 }}>
            <Box>
              <Typography level="body-sm" fontWeight="lg">
                {progress.uploaded} of {progress.total} photos uploaded successfully.
              </Typography>
              <Typography level="body-sm">
                {progress.failed} photo{progress.failed !== 1 ? 's' : ''} failed to upload.
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Success Display */}
        {progress.status === 'completed' && progress.uploaded > 0 && (
          <Alert color="success" variant="soft" sx={{ mb: 3 }}>
            <Typography level="body-sm">
              Successfully uploaded {progress.uploaded} photo{progress.uploaded !== 1 ? 's' : ''}.
            </Typography>
          </Alert>
        )}

        {/* File Drop Zone */}
        <Box
          sx={{
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'neutral.300',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: isUploading ? 'not-allowed' : 'pointer',
            bgcolor: dragActive ? 'primary.50' : 'background.level1',
            mb: 3,
            transition: 'all 0.2s ease',
            opacity: isUploading ? 0.5 : 1,
          }}
          onDragEnter={isUploading ? undefined : handleDrag}
          onDragLeave={isUploading ? undefined : handleDrag}
          onDragOver={isUploading ? undefined : handleDrag}
          onDrop={isUploading ? undefined : handleDrop}
          onClick={isUploading ? undefined : () => fileInputRef.current?.click()}
        >
          <Upload size={48} color="gray" style={{ marginBottom: 16 }} />
          <Typography level="body-lg" sx={{ mb: 1 }}>
            Drop photos here or click to browse
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Supports JPG, PNG, GIF up to 10MB each
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={isUploading}
          />
        </Box>

        {/* Description Input */}
        <FormControl sx={{ mb: 3 }}>
          <FormLabel>Description (optional)</FormLabel>
          <Input
            placeholder="Add a description for these photos..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isUploading}
          />
        </FormControl>

        {/* Selected Files */}
        {selectedFiles.length > 0 && (
          <>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Selected Photos ({selectedFiles.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3, maxHeight: 200, overflowY: 'auto' }}>
              {selectedFiles.map((file, index) => {
                const status = getFileStatus(file, index, progress);
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      border: '1px solid',
                      borderColor: status === 'failed' ? 'danger.300' : 'neutral.200',
                      borderRadius: 1,
                      bgcolor: status === 'failed' ? 'danger.softBg' : 'background.body',
                    }}
                  >
                    {/* Status icon */}
                    {status === 'success' && <Check size={20} color="var(--joy-palette-success-500)" />}
                    {status === 'failed' && <AlertCircle size={20} color="var(--joy-palette-danger-500)" />}
                    {status === 'uploading' && <CircularProgress size="sm" sx={{ '--CircularProgress-size': '20px' }} />}
                    {status === 'pending' && <FileImage size={20} />}

                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                        {file.name}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {formatFileSize(file.size)}
                      </Typography>
                    </Box>
                    {!isUploading && status !== 'success' && (
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => removeFile(index)}
                      >
                        <X size={16} />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
            <Divider />
          </>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
          <Button
            variant="plain"
            onClick={handleClose}
            disabled={isUploading}
          >
            {progress.status === 'partial_failure' ? 'Close' : 'Cancel'}
          </Button>

          {/* Retry button for failed uploads */}
          {progress.status === 'partial_failure' && progress.failedFiles.length > 0 && (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleRetry}
              disabled={isUploading}
              startDecorator={<RefreshCw size={18} />}
            >
              Retry Failed ({progress.failedFiles.length})
            </Button>
          )}

          {/* Main upload button */}
          {progress.status !== 'partial_failure' && progress.status !== 'completed' && (
            <Button
              variant="solid"
              color="primary"
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || isUploading}
              startDecorator={isUploading ? <CircularProgress size="sm" /> : <Upload size={18} />}
            >
              {isUploading ? 'Uploading...' : `Upload ${selectedFiles.length} Photo${selectedFiles.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </Box>
      </ModalDialog>
    </Modal>
  );
};
